/**
 * publishForm.js — règles des deux formulaires de publication (colis, trajet), en quatre étapes (direction C,
 * 2026-11-03, docs/design/ecarts-publication.md). Les écrans affichent ; ici : étapes, contrôles d'une étape,
 * contenu envoyé au serveur, brouillon. Les minimums de prix arrivent calculés (taux du jour), jamais recalculés ici.
 */
import { hasCardPayable } from '@/utils/currencyUtils';

// ─── Étapes ───────────────────────────────────────────────────────────────────
export const SHIPMENT_STEPS = [
  { key: 'route', label: 'Le trajet' },
  { key: 'parcel', label: 'Le colis' },
  { key: 'price', label: 'Le prix' },
  { key: 'details', label: 'Les détails', optional: true },
  { key: 'recap', label: 'Récapitulatif' },
];
export const TRIP_STEPS = [
  { key: 'route', label: 'Le trajet' },
  { key: 'space', label: 'La place' },
  { key: 'price', label: 'Le prix' },
  { key: 'details', label: 'Les détails', optional: true },
  { key: 'recap', label: 'Récapitulatif' },
];

// ─── Choix fermés (mêmes valeurs que backend/listing_options.py) ─────────────
export const FLEX_OPTIONS = [
  { id: 'fixe', label: 'Date fixe' },
  { id: 'pm3j', label: '± 3 jours' },
  { id: 'pm1s', label: '± 1 semaine' },
  { id: 'pm2s', label: '± 2 semaines' },
];
export const URGENCY_OPTIONS = [
  { id: 'standard', label: 'Pas pressé' },
  { id: 'priority', label: 'Prioritaire' },
  { id: 'urgent', label: 'Urgent' },
];

export const MAX_CHOICES = 5;
export const MAX_CURRENCIES = 3;

const num = (v) => (v === '' || v == null ? NaN : Number(String(v).replace(',', '.')));
const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

function routeErrors(f, dateLabel) {
  const e = {};
  if (!f.origCountry) e.origCountry = 'Choisissez le pays de départ.';
  else if (!f.origCity) e.origCity = 'Choisissez la ville de départ.';
  if (!f.destCountry) e.destCountry = "Choisissez le pays d'arrivée.";
  else if (!f.destCity) e.destCity = "Choisissez la ville d'arrivée.";
  if (f.origCity && f.destCity && f.origCountry === f.destCountry && same(f.origCity, f.destCity)) {
    e.destCity = "Le départ et l'arrivée doivent être différents.";
  }
  if (!f.date) e.date = `${dateLabel} est requise.`;
  return e;
}

function currencyErrors(f) {
  if (!f.currencies?.length) return { currencies: 'Choisissez au moins une devise.' };
  if (!hasCardPayable(f.currencies)) return { currencies: 'Ajoutez une devise payable par carte : le cedi seul ne permet pas de conclure.' };
  return {};
}

/**
 * Erreurs d'une étape du colis : { champ: message } (vide si l'étape est complète).
 * `ctx` : { maxWeight, minPrice, minPriceText }.
 */
export function shipmentStepErrors(step, f, ctx) {
  if (step === 'route') return routeErrors(f, 'La date limite');
  if (step === 'parcel') {
    const e = {};
    const w = num(f.weightKg);
    if (!(w >= 0.1 && w <= ctx.maxWeight)) e.weightKg = `Le poids doit être entre 0,1 et ${ctx.maxWeight.toLocaleString('fr-FR')} kg.`;
    if (f.categories.includes('Autre') && !f.categoryCustom.trim()) e.categoryCustom = 'Précisez la catégorie « Autre ».';
    return e;
  }
  if (step === 'price') {
    const e = currencyErrors(f);
    if (!f.budgetLibre && !(num(f.priceOffered) >= ctx.minPrice)) e.priceOffered = `Le budget doit être d'au moins ${ctx.minPriceText}.`;
    return e;
  }
  if (step === 'details') {
    return f.constraints.includes('other') && !f.constraintCustom.trim() ? { constraintCustom: 'Précisez la précaution « Autre ».' } : {};
  }
  return {};
}

/** Erreurs d'une étape du trajet. `ctx` : { maxWeight, priceMin, priceMax, priceRangeText, fixedMinText }. */
export function tripStepErrors(step, f, ctx) {
  if (step === 'route') {
    const e = routeErrors(f, 'La date de départ');
    if (!f.transports.length) e.transports = 'Choisissez au moins un moyen de transport.';
    else if (f.transports.includes('autre') && !f.transportCustom.trim()) e.transportCustom = 'Précisez le moyen de transport « Autre ».';
    return e;
  }
  if (step === 'space') {
    const e = {};
    const cap = num(f.capacity);
    if (!(cap >= 0.1 && cap <= ctx.maxWeight)) e.capacity = `La place libre doit être entre 0,1 et ${ctx.maxWeight.toLocaleString('fr-FR')} kg.`;
    const perParcel = num(f.maxPerParcel);
    if (f.maxPerParcel !== '' && !(perParcel >= 0.1 && (!(cap > 0) || perParcel <= cap))) e.maxPerParcel = 'Le poids par colis doit être entre 0,1 kg et la place libre.';
    if (f.categories.includes('Autre') && !f.categoryCustom.trim()) e.categoryCustom = 'Précisez la catégorie « Autre ».';
    return e;
  }
  if (step === 'price') {
    const e = currencyErrors(f);
    const p = num(f.mode === 'fixe' ? f.priceFixed : f.pricePerKg);
    if (f.mode === 'kg' && !(p >= ctx.priceMin && p <= ctx.priceMax)) e.pricePerKg = `Le tarif doit être entre ${ctx.priceRangeText}.`;
    if (f.mode === 'fixe' && !(p >= ctx.priceMin)) e.priceFixed = `Le forfait doit être d'au moins ${ctx.fixedMinText}.`;
    return e;
  }
  return {};
}

/** Première étape incomplète (avant le récapitulatif), ou null : sert au bouton « Publier ». */
export function firstInvalidStep(steps, errorsOf) {
  const found = steps.filter((s) => s.key !== 'recap').find((s) => Object.keys(errorsOf(s.key)).length > 0);
  return found ? found.key : null;
}

/** Ajoute ou retire un choix d'une liste limitée ; renvoie la liste inchangée si la limite est atteinte. */
export function toggleChoice(list, id, max = MAX_CHOICES) {
  if (list.includes(id)) return list.filter((x) => x !== id);
  return list.length >= max ? list : [...list, id];
}

const dims = (l, w, h, sep = '×') => ((l || w || h) ? `${l || '?'}${sep}${w || '?'}${sep}${h || '?'} cm` : '');
const withCustom = (list, custom) => (list.includes('Autre') && custom.trim() ? [...list.filter((c) => c !== 'Autre'), custom.trim()] : [...list]);

/** Corps de POST / PATCH /shipments, tel que le serveur l'attend. */
export function shipmentPayload(f) {
  const categories = withCustom(f.categories, f.categoryCustom);
  return {
    title: (f.title.trim() || `Colis ${f.origCity} → ${f.destCity}`).slice(0, 50),
    description: (f.description.trim() || (categories.length ? `Catégories : ${categories.join(', ')}` : 'Colis personnel')).slice(0, 120),
    departure_city: f.origCity,
    arrival_city: f.destCity,
    departure_country: f.origCountry,
    arrival_country: f.destCountry,
    departure_date: new Date(f.date).toISOString(),
    flex_departure: f.flex,
    urgency: f.urgency,
    weight_kg: num(f.weightKg),
    dimensions: dims(f.dimL, f.dimW, f.dimH),
    price_offered: f.budgetLibre ? null : num(f.priceOffered),
    primary_currency: f.primaryCurrency,
    currencies: f.currencies,
    categories,
    category_custom: f.categoryCustom || null,
    constraints: f.constraints,
    constraint_custom: f.constraintCustom || null,
    carrier_preferences: f.carrierPreferences,
    message_to_carriers: f.message.trim() || null,
    counter_offers: f.counterOffers,
    photos: f.photos.map((p) => p.dataUrl),
  };
}

/** Corps de POST / PATCH /trips. Forfait : le serveur garde aussi un prix au kilo équivalent (prix ÷ place). */
export function tripPayload(f) {
  const capacity = num(f.capacity);
  let pricePerKg = 0;
  if (f.mode === 'kg') pricePerKg = num(f.pricePerKg);
  else if (f.mode === 'fixe') pricePerKg = num(f.priceFixed) / capacity;
  return {
    departure_city: f.origCity,
    arrival_city: f.destCity,
    departure_country: f.origCountry,
    arrival_country: f.destCountry,
    departure_date: new Date(f.date).toISOString(),
    arrival_date: f.arrivalDate ? new Date(f.arrivalDate).toISOString() : null,
    flex_departure: f.flex,
    transport_modes: f.transports,
    transport_custom: f.transportCustom || null,
    available_weight_kg: capacity,
    max_weight_per_parcel: f.maxPerParcel !== '' ? num(f.maxPerParcel) : null,
    max_parcels: f.maxParcels !== '' ? parseInt(f.maxParcels, 10) : null,
    max_dimensions: dims(f.dimL, f.dimW, f.dimH, ' × ') || null,
    accepted_categories: withCustom(f.categories, f.categoryCustom),
    category_custom: f.categoryCustom || null,
    pricing_mode: f.mode,
    price_per_kg: pricePerKg,
    price_fixed: f.mode === 'fixe' ? num(f.priceFixed) : null,
    currencies: f.currencies,
    primary_currency: f.primaryCurrency,
    counter_offers: f.mode === 'libre' ? true : f.counterOffers,
    conditions: f.conditions,
    description: f.description.trim() || `Trajet ${f.origCity} → ${f.destCity}`,
    message_to_senders: f.message.trim() || null,
  };
}

/** Souplesse d'une annonce publiée avant le 2026-11-03 (« souple », « libre ») : relue dans le nouveau vocabulaire. */
export function normalizeFlex(code) {
  if (code === 'souple') return 'pm3j';
  if (code === 'libre') return 'pm2s';
  return FLEX_OPTIONS.some((o) => o.id === code) ? code : 'fixe';
}

// ─── Brouillon (navigateur, le temps de la session) ──────────────────────────
// Une photo pèse plusieurs centaines de Ko : le brouillon ne les garde pas (le stockage de session est limité).
export function readDraft(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveDraft(key, form) {
  try { sessionStorage.setItem(key, JSON.stringify({ ...form, photos: [] })); } catch { /* stockage indisponible */ }
}
export function clearDraft(key) {
  try { sessionStorage.removeItem(key); } catch { /* stockage indisponible */ }
}
